import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { BuildStep, CustomerDraft } from '@/types'

const DEFAULT_CUSTOMER: CustomerDraft = {
  name: '',
  email: '',
  phone: '',
  location: '',
  timeline: '',
  budget_range: '',
  financing_interest: false,
  contact_method: '',
  notes: '',
}

interface BuildState {
  selectedModelId: number | null
  selectedOptionIds: number[]
  currentStep: BuildStep
  currentCategoryIndex: number
  customerDraft: CustomerDraft
  submittedLeadId: number | null
  submittedTotal: number | null
  snapshotDataUrl: string | null

  selectModel: (id: number) => void
  setSelectedOptionIds: (ids: number[]) => void
  setStep: (step: BuildStep) => void
  setCategoryIndex: (index: number) => void
  setCustomerField: (field: keyof CustomerDraft, value: string | boolean) => void
  setSubmitted: (leadId: number, total: number) => void
  setSnapshotDataUrl: (url: string | null) => void
  resetBuild: () => void
}

export const useBuildStore = create<BuildState>()(
  persist(
    (set) => ({
      selectedModelId: null,
      selectedOptionIds: [],
      currentStep: 'landing',
      currentCategoryIndex: 0,
      customerDraft: DEFAULT_CUSTOMER,
      submittedLeadId: null,
      submittedTotal: null,
      snapshotDataUrl: null,

      selectModel: (id) =>
        set({ selectedModelId: id, selectedOptionIds: [], currentCategoryIndex: 0 }),

      setSelectedOptionIds: (ids) => set({ selectedOptionIds: ids }),

      setStep: (step) => set({ currentStep: step }),

      setCategoryIndex: (index) => set({ currentCategoryIndex: index }),

      setCustomerField: (field, value) =>
        set((state) => ({
          customerDraft: { ...state.customerDraft, [field]: value },
        })),

      setSubmitted: (leadId, total) =>
        set({ submittedLeadId: leadId, submittedTotal: total, currentStep: 'thanks' }),

      setSnapshotDataUrl: (url) => set({ snapshotDataUrl: url }),

      resetBuild: () =>
        set({
          selectedModelId: null,
          selectedOptionIds: [],
          currentStep: 'landing',
          currentCategoryIndex: 0,
          customerDraft: DEFAULT_CUSTOMER,
          submittedLeadId: null,
          submittedTotal: null,
          snapshotDataUrl: null,
        }),
    }),
    {
      name: 'lm-build-state',
      partialize: (state) => ({
        selectedModelId: state.selectedModelId,
        selectedOptionIds: state.selectedOptionIds,
        currentStep: state.currentStep,
        currentCategoryIndex: state.currentCategoryIndex,
        customerDraft: state.customerDraft,
      }),
    }
  )
)
